import { Controller } from "@hotwired/stimulus"

/**
 * Browse Map Controller
 *
 * Renders a Mapbox GL map with price-bubble markers for each property
 * on the current search results page. Supports card ↔ marker hover
 * highlighting and marker click popups.
 *
 * When the user pans or zooms the map, new experiences are fetched
 * for the visible viewport and the card list + markers are updated.
 */

declare const mapboxgl: any

interface MapMarker {
  lat: number
  lng: number
  price: number
  uuid: string
  name: string
}

export default class extends Controller {
  static targets = ["map", "data", "card", "tokenTemplate"]

  declare mapTarget: HTMLElement
  declare dataTarget: HTMLElement
  declare cardTargets: HTMLElement[]
  declare tokenTemplateTarget: HTMLElement
  declare hasMapTarget: boolean

  private map: any = null
  private markerElements: HTMLElement[] = []
  private mapboxMarkers: any[] = []
  private markers: MapMarker[] = []
  private moveEndTimer: ReturnType<typeof setTimeout> | null = null
  private isUserInteraction: boolean = false
  private isFetchingBounds: boolean = false

  connect(): void {
    // Wait for mapbox script to load
    if (typeof mapboxgl === "undefined") {
      // Retry after a short delay for script loading
      setTimeout(() => this.initMap(), 200)
      return
    }
    this.initMap()
  }

  disconnect(): void {
    if (this.moveEndTimer) {
      clearTimeout(this.moveEndTimer)
    }
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  private initMap(): void {
    if (!this.hasMapTarget || typeof mapboxgl === "undefined") return

    let token: string | undefined
    try {
      token = JSON.parse(this.tokenTemplateTarget.textContent || "")
    } catch {
      return
    }
    if (!token) return

    // Parse marker data
    try {
      this.markers = JSON.parse(this.dataTarget.textContent || "[]")
    } catch {
      this.markers = []
    }

    mapboxgl.accessToken = token

    const mapContainer = this.mapTarget.querySelector("#browse-map")
    if (!mapContainer) return

    this.map = new mapboxgl.Map({
      container: mapContainer as HTMLElement,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-98.5, 39.8], // Default US center
      zoom: 3,
      attributionControl: true,
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this.map.on("load", () => {
      this.addMarkers()
      this.fitBounds()

      // Start listening for user-initiated map moves after initial fit
      this.map.once("idle", () => {
        this.isUserInteraction = true
        this.map.on("moveend", () => this.onMapMoveEnd())
      })
    })
  }

  private onMapMoveEnd(): void {
    if (!this.isUserInteraction) return

    // Debounce: wait 500ms after the last moveend before fetching
    if (this.moveEndTimer) {
      clearTimeout(this.moveEndTimer)
    }
    this.moveEndTimer = setTimeout(() => this.fetchForBounds(), 500)
  }

  private fetchForBounds(): void {
    if (!this.map || this.isFetchingBounds) return

    const bounds = this.map.getBounds()
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()

    // Build URL with current search params + bounds
    const url = new URL(window.location.href)
    url.searchParams.set("bounds_sw_lat", sw.lat.toFixed(6))
    url.searchParams.set("bounds_sw_lng", sw.lng.toFixed(6))
    url.searchParams.set("bounds_ne_lat", ne.lat.toFixed(6))
    url.searchParams.set("bounds_ne_lng", ne.lng.toFixed(6))
    url.searchParams.delete("page")

    this.isFetchingBounds = true

    fetch(url.toString(), {
      headers: {
        "HX-Request": "true",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        // Update experience cards
        const container = document.getElementById("experiences-container")
        if (container) {
          // Parse the response to extract cards and markers
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, "text/html")

          const newContainer = doc.getElementById("experiences-container")
          if (newContainer) {
            container.innerHTML = newContainer.innerHTML
          }

          // Extract updated markers from the response
          const markersScript = doc.getElementById("map-markers-update")
          if (markersScript) {
            try {
              const newMarkers: MapMarker[] = JSON.parse(markersScript.textContent || "[]")
              this.updateMarkers(newMarkers)
            } catch {
              // Keep existing markers if parse fails
            }
          }
        }
      })
      .catch(() => {
        // Silently fail — user can still interact with existing results
      })
      .finally(() => {
        this.isFetchingBounds = false
      })
  }

  private updateMarkers(newMarkers: MapMarker[]): void {
    // Remove existing markers
    this.mapboxMarkers.forEach((m) => m.remove())
    this.mapboxMarkers = []
    this.markerElements = []
    this.markers = newMarkers

    // Add new markers without moving the map
    this.addMarkers()
  }

  private addMarkers(): void {
    this.markers.forEach((marker, index) => {
      const priceFormatted = `$${Math.round(marker.price / 100)}`

      // Create price bubble element
      const el = document.createElement("div")
      el.className = "map-price-marker"
      el.textContent = priceFormatted
      el.dataset.index = String(index)

      el.addEventListener("click", () => this.onMarkerClick(marker, el))

      const mapboxMarker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([marker.lng, marker.lat])
        .addTo(this.map)

      this.markerElements.push(el)
      this.mapboxMarkers.push(mapboxMarker)
    })
  }

  private fitBounds(): void {
    if (this.markers.length === 0) return

    if (this.markers.length === 1) {
      this.map.flyTo({
        center: [this.markers[0].lng, this.markers[0].lat],
        zoom: 12,
      })
      return
    }

    const bounds = new mapboxgl.LngLatBounds()
    this.markers.forEach((m) => bounds.extend([m.lng, m.lat]))
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 14 })
  }

  private onMarkerClick(marker: MapMarker, el: HTMLElement): void {
    // Close any existing popups
    const existingPopups = document.querySelectorAll(".mapboxgl-popup")
    existingPopups.forEach((p) => p.remove())

    const priceFormatted = `$${Math.round(marker.price / 100)}`

    new mapboxgl.Popup({ offset: 25, closeButton: true })
      .setLngLat([marker.lng, marker.lat])
      .setHTML(
        `<div style="font-family:inherit;text-align:center;padding:4px">` +
          `<div style="font-weight:600;font-size:14px;margin-bottom:2px">${marker.name}</div>` +
          `<div style="color:#706B67;font-size:13px;margin-bottom:6px">${priceFormatted}/night</div>` +
          `<a href="/experiences/${marker.uuid}" style="color:#020D19;font-weight:600;font-size:13px;text-decoration:underline">View</a>` +
          `</div>`,
      )
      .addTo(this.map)

    // Scroll to and highlight the corresponding card
    const index = parseInt(el.dataset.index || "0", 10)
    const card = this.cardTargets[index]
    if (card) {
      const inner = card.querySelector(".card-experience") || card
      // Remove highlight from all cards
      document.querySelectorAll(".browse-map-active-card").forEach((c) => c.classList.remove("browse-map-active-card"))
      // Highlight the inner card element
      inner.classList.add("browse-map-active-card")
      // Scroll the card into view
      card.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  // Called from card mouseenter via Stimulus action
  highlight(event: Event): void {
    const target = event.currentTarget as HTMLElement
    const index = parseInt(target.dataset.browseMapIndexParam || target.dataset.index || "0", 10)
    const el = this.markerElements[index]
    if (el) {
      el.classList.add("active")
    }
  }

  // Called from card mouseleave via Stimulus action
  unhighlight(event: Event): void {
    const target = event.currentTarget as HTMLElement
    const index = parseInt(target.dataset.browseMapIndexParam || target.dataset.index || "0", 10)
    const el = this.markerElements[index]
    if (el) {
      el.classList.remove("active")
    }
  }
}
